/* ---------------------------------------------------------------------------
   DreamPrices – varukorgslådan (mini-cart).  Stabiliserad 2026-08-08.
   Uppdaterad 2026-08-12: skydd mot för tidig auto-stängning.

   Fyra problem åtgärdade:

   1) "Ibland måste man lägga till produkten två gånger."
      När lådan är öppen lägger temat klassen `added__overlay` på <body>. Dess
      ::before är ett heltäckande KLICKBART lager (z-index 99) som slök nästa
      klick: klicket stängde lådan i stället för att träffa "Lägg i varukorgen"
      bakom. Lådan sätter nu även klassen `dp-cart-open` på <body>, och CSS:en i
      snippets/cart-notification.liquid gör överlägget genomklickbart medan
      lådan är öppen. Ett klick räcker igen. Sök-/filteröverläggen påverkas ej.

   2) "Segt."
      Lådan öppnades först när servern svarat (0,5–1,5 s helt utan återkoppling).
      Nu öppnas den DIREKT med ett laddningsläge och fylls på när svaret kommer.

   3) Instabilitet.
      renderContents() kastade fel om en sektion eller ett element saknades –
      då öppnades lådan aldrig trots att varan LÅG i varukorgen. Allt är nu
      defensivt: lådan öppnas även om en del misslyckas, svar som kommer i fel
      ordning kastas bort, och fokusfällan (trapFocus) sätts inte längre kvar
      efter att lådan stängts.

   4) "Lådan försvinner för fort, av sig själv" (Jonas 2026-08-12).
      handleBodyClick stänger lådan vid VILKET klick som helst på <body> som
      inte träffar lådan/varukorgsikonen – men den skyddade inte mot klick
      eller syntetiska events som INTE kommer från kunden (t.ex. tredjeparts-
      skript för cookie-samtycke/spårning som ofta initierar sig själva någon
      sekund efter sidladdning och kan trigga klick-liknande DOM-events).
      En riktig person hinner aldrig avsiktligt klicka utanför lådan inom
      några hundra millisekunder efter att den öppnats, så vi ignorerar nu
      "klick utanför"-stängning under en kort skyddstid direkt efter öppning.
      Kostar ingenting för äkta användning, blockerar för tidiga stängningar
      oavsett vad som orsakar dem.
   --------------------------------------------------------------------------- */

window.DP_CART_SECTIONS = [
  "cart-notification-product",
  "cart-notification-subtotal",
  "cart-notification-count",
  "cart-notification-discount",
];

const DP_CART_CLOSE_GRACE_MS = 600;

class CartRemoveButton2 extends HTMLElement {
  constructor() {
    super();
    this.addEventListener("click", (event) => {
      event.preventDefault();

      const row = this.closest(".cart-notification-product");
      if (row) {
        row.classList.add("loading");
        if (row.firstElementChild) row.firstElementChild.classList.remove("hidden");
      }

      const cart = this.closest("cart-notification") || document.querySelector("cart-notification");
      if (cart && typeof cart.updateQuantity === "function") {
        cart.updateQuantity(this.dataset.index, 0);
      }
    });
  }
}
if (!customElements.get("cart-remove-button-2")) {
  customElements.define("cart-remove-button-2", CartRemoveButton2);
}

class CartNotification extends HTMLElement {
  constructor() {
    super();

    this.notification = document.getElementById("offcanvas__mini_cart") || this;
    this.header = document.querySelector("sticky-header");

    this.onBodyClick = this.handleBodyClick.bind(this);
    this.requestSeq = 0;
    this.bindTimer = null;
    this.focusTimer = null;
    this.openedAt = 0;

    this.notification.addEventListener(
      "keyup",
      (evt) => evt.code === "Escape" && this.close()
    );

    /* Delegerat: stäng-krysset OCH "Fortsätt handla" stänger bara lådan –
       ingen navigering, kunden blir kvar på sidan hen var på. */
    this.addEventListener("click", (evt) => {
      const closer =
        evt.target.closest &&
        evt.target.closest(".cart-notification__close, [data-dp-continue]");
      if (!closer) return;
      evt.preventDefault();
      this.close();
    });

    this.debouncedOnChange =
      typeof debounce === "function"
        ? debounce((event) => this.onChange(event), 300)
        : (event) => this.onChange(event);

    this.addEventListener("change", this.debouncedOnChange);
  }

  /* --- öppna / stänga ----------------------------------------------------- */

  open(loading) {
    const drawer = this.notification;
    if (!drawer) return;

    this.setLoading(loading === true);
    drawer.classList.add("animate", "active");
    document.body.classList.add("added__overlay", "dp-cart-open");
    this.openedAt = Date.now();

    /* Bind "klick utanför stänger" först på nästa tick – annars stänger samma
       klick som öppnade lådan den direkt igen. */
    document.body.removeEventListener("click", this.onBodyClick);
    clearTimeout(this.bindTimer);
    this.bindTimer = setTimeout(() => {
      document.body.addEventListener("click", this.onBodyClick);
    }, 0);

    if (!this.focusTimer) {
      this.focusTimer = setTimeout(() => {
        this.focusTimer = null;
        if (!drawer.classList.contains("active")) return;
        try {
          drawer.focus({ preventScroll: true });
        } catch (e) {
          /* fokus är trevligt men inte kritiskt */
        }
      }, 300);
    }
  }

  close() {
    const drawer = this.notification;
    if (drawer) drawer.classList.remove("active");
    document.body.classList.remove("added__overlay", "dp-cart-open");

    this.setLoading(false);
    clearTimeout(this.bindTimer);
    clearTimeout(this.focusTimer);
    this.focusTimer = null;
    document.body.removeEventListener("click", this.onBodyClick);
  }

  setLoading(on) {
    if (this.notification) this.notification.classList.toggle("dp-cart-loading", !!on);
  }

  handleBodyClick(evt) {
    const drawer = this.notification;
    if (!drawer || !drawer.classList.contains("active")) return;

    // Background scripts may dispatch click events long after opening.
    // Only an actual user click may dismiss the cart from outside.
    if (!evt.isTrusted) return;

    /* Skydd mot för tidig stängning (se punkt 4 i filhuvudet ovan) – ett
       klick/event som når hit inom kort efter öppning ignoreras, oavsett
       varifrån det kommer. */
    if (this.openedAt && Date.now() - this.openedAt < DP_CART_CLOSE_GRACE_MS) {
      return;
    }

    const target = evt.target;
    if (!target || typeof target.closest !== "function") return;
    if (
      target.closest("cart-notification") ||
      target.closest("open-minicart") ||
      target.closest("a.header__actions_btn--cart") ||
      target.closest("#quickViewWrapper") ||
      target.closest('[data-type="add-to-cart-form"]') ||
      target.closest(".dp-buybox-kopnu-btn")
    ) {
      return;
    }
    this.close();
  }

  /* --- rendering ---------------------------------------------------------- */

  getSectionsToRender() {
    return window.DP_CART_SECTIONS.map((id) => ({ id }));
  }

  getSectionInnerHTML(html, selector = ".shopify-section") {
    if (typeof html !== "string") return null;
    try {
      const found = new DOMParser().parseFromString(html, "text/html").querySelector(selector);
      return found ? found.innerHTML : html;
    } catch (e) {
      return null;
    }
  }

  renderSections(sections) {
    if (!sections) return;
    window.DP_CART_SECTIONS.forEach((id) => {
      const target = document.getElementById(id);
      if (!target) return; // finns inte på den här sidan – hoppa över i stället för att krascha
      const inner = this.getSectionInnerHTML(sections[id]);
      if (inner === null) return; // sektionen kom inte med i svaret – behåll det gamla
      target.innerHTML = inner;
    });
  }

  toggleEmptyState(itemCount) {
    const isEmpty = itemCount === 0; // okänt antal (t.ex. svar från /cart/add.js) = inte tom
    const hide = (el, shouldHide) => {
      if (el) el.classList.toggle("no-js-inline", shouldHide);
    };
    hide(document.getElementById("min-cart-items"), isEmpty);
    hide(document.getElementById("empty__cart__button"), isEmpty);
    hide(document.querySelector(".empty__cart__item"), !isEmpty);
    hide(document.querySelector(".item__success_message"), isEmpty);
    hide(document.querySelector(".item__empty_message"), !isEmpty);
  }

  renderContents(parsedState) {
    try {
      this.renderSections(parsedState && parsedState.sections);
      this.toggleEmptyState(parsedState ? parsedState.item_count : undefined);
    } catch (e) {
      console.error("[DreamPrices] mini-cart render:", e);
    }

    if (this.header && typeof this.header.reveal === "function") {
      try {
        this.header.reveal();
      } catch (e) {
        /* ignorera */
      }
    }

    this.setLoading(false);
    this.open(); // öppnas ALLTID, även om något ovan gick fel
  }

  /* --- antal / ta bort ---------------------------------------------------- */

  onChange(event) {
    if (event.target.closest("select")) return;
    this.updateQuantity(
      event.target.dataset.index,
      event.target.value,
      document.activeElement ? document.activeElement.getAttribute("name") : null,
      event.target.dataset.quantityVariantId
    );
  }

  updateQuantity(line, quantity, name, variantId) {
    const seq = ++this.requestSeq;
    this.setLoading(true);

    const body = JSON.stringify({
      line: line,
      quantity: quantity,
      sections: window.DP_CART_SECTIONS,
      sections_url: window.location.pathname,
    });

    const config =
      typeof fetchConfig === "function"
        ? fetchConfig()
        : { method: "POST", headers: { "Content-Type": "application/json", Accept: "application/json" } };

    fetch(routes.cart_change_url, Object.assign({}, config, { body: body }))
      .then((response) => response.json())
      .then((parsedState) => {
        if (seq !== this.requestSeq) return; // ett nyare anrop har hunnit före – kasta svaret

        const quantityElement = document.getElementById(`Quantity-${line}`);
        const items = document.querySelectorAll(".cart-notification-product");

        if (parsedState.errors) {
          if (quantityElement) quantityElement.value = quantityElement.getAttribute("value");
          this.updateLiveRegions(line, parsedState.errors);
          this.setLoading(false);
          return;
        }

        this.renderContents(parsedState);

        const updatedValue =
          parsedState.items && parsedState.items[line - 1]
            ? parsedState.items[line - 1].quantity
            : undefined;

        let message = "";
        if (
          quantityElement &&
          parsedState.items &&
          items.length === parsedState.items.length &&
          updatedValue !== parseInt(quantityElement.value, 10)
        ) {
          const strings = window.cartStrings || {};
          message =
            typeof updatedValue === "undefined"
              ? strings.error || ""
              : (strings.quantityError || "").replace("[quantity]", updatedValue);
        }
        this.updateLiveRegions(line, message);

        if (typeof publish === "function" && typeof PUB_SUB_EVENTS === "object") {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: "cart-notification",
            cartData: parsedState,
            variantId: variantId,
          });
        }
      })
      .catch((e) => {
        console.error("[DreamPrices] mini-cart uppdatering:", e);
      })
      .finally(() => {
        if (seq === this.requestSeq) this.setLoading(false);
      });
  }

  updateLiveRegions(line, message) {
    const lineItemError = document.getElementById(`Line-item-error-${line}`);
    if (!lineItemError) return;
    const text = lineItemError.querySelector(".cart-item__error-text");
    if (!text) return;
    lineItemError.classList.remove("d-none");
    text.innerHTML = message;
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

if (!customElements.get("cart-notification")) {
  customElements.define("cart-notification", CartNotification);
}

class OpenMiniCart extends HTMLElement {
  constructor() {
    super();
    /* Själva klicket sköts av den delegerade lyssnaren nedan – den fungerar
       även för varukorgsikoner som ligger utanför <open-minicart>. */
  }

  onClickMiniCart() {
    const cart = document.querySelector("cart-notification");
    if (cart && typeof cart.open === "function") cart.open();
  }

  miniCartClose() {
    const cart = document.querySelector("cart-notification");
    if (cart && typeof cart.close === "function") cart.close();
  }
}
if (!customElements.get("open-minicart")) {
  customElements.define("open-minicart", OpenMiniCart);
}

/* ---------------------------------------------------------------------------
   Varukorgsikonen: öppna lådan DIREKT och hämta färskt innehåll i bakgrunden.
   Tidigare väntade den på två nätverksanrop innan något syntes på skärmen –
   det var det som upplevdes som segt.
   --------------------------------------------------------------------------- */
(function () {
  if (window.__dpCartIconBound) return;
  window.__dpCartIconBound = true;
  window.__dpCartFreshOpen = true; // spärr mot äldre inline-variant av samma fix

  let inFlight = 0;

  document.addEventListener(
    "click",
    function (event) {
      const trigger =
        event.target.closest &&
        event.target.closest("open-minicart a, a.header__actions_btn--cart");
      if (!trigger) return;

      const cart = document.querySelector("cart-notification");
      if (!cart || typeof cart.renderContents !== "function") return; // fall tillbaka på /cart

      event.preventDefault();
      event.stopImmediatePropagation();

      cart.open(true); // syns omedelbart

      const seq = ++inFlight;
      Promise.all([
        fetch("/?sections=" + window.DP_CART_SECTIONS.join(",")).then((r) => r.json()),
        fetch("/cart.js").then((r) => r.json()),
      ])
        .then(function (result) {
          if (seq !== inFlight) return;
          cart.renderContents({ sections: result[0], item_count: result[1].item_count });
        })
        .catch(function () {
          if (seq === inFlight) cart.setLoading(false); // lådan är redan öppen med sitt gamla innehåll
        });
    },
    true
  );
})();
